import { PersonDuplicates } from '@fs/zion-icon'
import {
  Button,
  Cell,
  DialogOverlay,
  DialogOverlayActions,
  DialogOverlayFooter,
  Grid,
  Radio,
  RadioGroup,
  Select,
  TextField,
} from '@fs/zion-ui'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useNotifications from '../../../../hooks/useNotifications'
import { createPerson } from '../../../../services/famousHistoricalService'
import DmcDataView from '../../../common/DmcDataView'
import { DmcDatePicker } from '../../../common/DmcDatePicker/DmcDatePicker'

const defaultPersonObject = { birthDateItem: { key: 'birthDateItem', primaryText: '' } }

export default function HistoricalCreatePersonDialog({ countries, ...overlay }) {
  const { t } = useTranslation()
  const { error } = useNotifications()
  const [person, setPerson] = useState(defaultPersonObject)
  const [country, setCountry] = useState()
  const [alternatePerson, setAlternatePerson] = useState()
  const data = getHistoricalDataForm()
  const givenNameTextBoxRef = useRef()

  useEffect(() => {
    if (overlay.isOpen && !overlay.transitioning) {
      givenNameTextBoxRef.current.focus()
    }
  }, [overlay.isOpen, overlay.transitioning])

  function shouldDisableSubmitButtons() {
    if (alternatePerson) {
      if (JSON.stringify(alternatePerson) === JSON.stringify(person)) {
        return true
      }
    }
    return Object.keys(person).length === 1 && !person.birthDateItem?.formattedDate
  }

  const submitBtnOnClickHandler = async (isSubmitBtn) => {
    const personWithFormattedDates = {
      ...person,
      birthDate: person.birthDateItem && person.birthDateItem.formattedDate,
    }
    delete personWithFormattedDates.birthDateItem

    const personDto = {
      metaDataId: 2,
      personJSON: JSON.stringify(personWithFormattedDates),
      matchType: 'HISTORICAL',
      country,
    }
    try {
      await createPerson(personDto)
      if (isSubmitBtn) {
        setPerson(defaultPersonObject)
        setAlternatePerson()
      } else {
        setAlternatePerson(JSON.parse(JSON.stringify(person)))
      }
      givenNameTextBoxRef.current.select()
    } catch (err) {
      error(`Error creating historical person: ${err.message || ''}`, err)
    }
  }

  const cancelBtnOnClickHandler = () => {
    setPerson(defaultPersonObject)
    setCountry()
    overlay.close()
  }

  const onPersonChange = (fieldType, newValue) => {
    const newPerson = { ...person, [fieldType]: newValue }
    if (newValue === '') {
      delete newPerson[fieldType]
    }
    setPerson(newPerson)
  }

  function getHistoricalDataForm() {
    return [
      {
        key: 'historicalGivenName',
        label: <b>{t('givenName', 'Given Name')}</b>,
        detail: (
          <TextField
            type="text"
            value={person.givenName}
            onChange={(e) => onPersonChange('givenName', e.target.value)}
            ref={(element) => {
              givenNameTextBoxRef.current = element
            }}
          />
        ),
      },
      {
        key: 'historicalSurname',
        label: <b>{t('name.parts.surname', 'Surname')}</b>,
        detail: (
          <TextField type="text" value={person.surname} onChange={(e) => onPersonChange('surname', e.target.value)} />
        ),
      },
      {
        key: 'historicalPersonId',
        label: <b>{t('personId', 'Person ID')}</b>,
        detail: <TextField type="text" value={person.pid} onChange={(e) => onPersonChange('pid', e.target.value)} />,
      },
      {
        key: 'historicalLabel',
        label: <b>{t('label', 'Label')}</b>,
        detail: (
          <Select
            placeholder={t('tools.lists.placeholders.selectLabel', 'Select Label')}
            value={person.label}
            onChange={(e) => onPersonChange('label', e.target.value)}
            options={[
              {
                value: 'Historical',
                label: t('historical', 'Historical'),
              },
              {
                value: 'Church Leader',
                label: t('churchLeader', 'Church Leader'),
              },
            ]}
          />
        ),
      },
      {
        key: 'historicalWhyFamous',
        label: <b>{t('tools.lists.whyFamous', 'Why Famous')}</b>,
        detail: (
          <TextField
            type="text"
            value={person.reasonWhyFamous}
            onChange={(e) => onPersonChange('reasonWhyFamous', e.target.value)}
          />
        ),
      },
      {
        key: 'historicalBirthDate',
        label: null,
        detail: (
          <DmcDatePicker
            label={t('tools.lists.labels.birthDate', 'Birth Date')}
            selectedItem={person.birthDateItem}
            setSelectedItem={(e) => onPersonChange('birthDateItem', e)}
            dayMonthYearDateFormat="DD MMM YYYY"
            monthYearDateFormat="yyyy"
            yearDateFormat="yyyy"
          />
        ),
      },
      {
        key: 'historicalNeedsSources',
        label: <b>{t('tools.lists.labels.needsSources', 'Needs Sources')}</b>,
        detail: (
          <RadioGroup orientation="horizontal">
            <Radio
              name="historicalNeedsSourcesRadioNo"
              checked={person.needsSources === true}
              label={t('yes', 'Yes')}
              value="true"
              onChange={(e) => onPersonChange('needsSources', e.target.value === 'true')}
            />
            <Radio
              name="historicalNeedsSourcesRadioNo"
              checked={person.needsSources === false}
              label={t('no', 'No')}
              value="false"
              onChange={(e) => onPersonChange('needsSources', e.target.value === 'true')}
            />
          </RadioGroup>
        ),
      },
      {
        key: 'historicalLocked',
        label: <b>{t('accessControl.locked', 'Locked')}</b>,
        detail: (
          <RadioGroup orientation="horizontal">
            <Radio
              name="historicalLockedRadioYes"
              checked={person.locked === true}
              label={t('yes', 'Yes')}
              value="true"
              onChange={(e) => onPersonChange('locked', e.target.value === 'true')}
            />
            <Radio
              name="historicalLockedRadioNo"
              checked={person.locked === false}
              label={t('no', 'No')}
              value="false"
              onChange={(e) => onPersonChange('locked', e.target.value === 'true')}
            />
          </RadioGroup>
        ),
      },
      {
        key: 'historicalPossibleDuplicates',
        label: <b>{t('tools.lists.labels.possibleDuplicates', 'Possible Duplicates')}</b>,
        detail: (
          <TextField
            type="text"
            value={person?.possibleDuplicates?.replaceAll('"', '')}
            onChange={(e) => onPersonChange('possibleDuplicates', e.target.value)}
          />
        ),
      },
    ]
  }

  return (
    <DialogOverlay
      {...overlay}
      title={t('tools.lists.headings.createNewHistoricalPerson', 'Create New Historical Person')}
      size="xxl"
    >
      <Grid>
        <Cell columns={3}>
          <Select
            id="country"
            placeholder={t('tools.lists.placeholders.selectMultiAreaTeam', 'Select Multi-area Team')}
            label={t('tools.lists.labels.multiAreaTeam', 'Multi-area Team')}
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            options={countries}
          />
        </Cell>
        <p columns={9} />
      </Grid>
      <DmcDataView data={data} />

      <DialogOverlayFooter>
        <DialogOverlayActions>
          <Button emphasis="high" onClick={() => submitBtnOnClickHandler(true)} disabled={shouldDisableSubmitButtons()}>
            {t('action.submit', 'Submit')}
          </Button>
          <Button
            Icon={PersonDuplicates}
            emphasis="high"
            onClick={() => submitBtnOnClickHandler(false)}
            disabled={shouldDisableSubmitButtons()}
          >
            {t('name.alternate', 'Alternate Name')}
          </Button>
          <Button emphasis="medium" onClick={cancelBtnOnClickHandler}>
            {t('action.cancel', 'Cancel')}
          </Button>
        </DialogOverlayActions>
      </DialogOverlayFooter>
    </DialogOverlay>
  )
}
